import React from "react";

import cls from "./Flex.module.scss";
import { classNames } from "../../../lib/classNames/classNames";
import { useFlexibleResponsiveValue } from "../../../../shared/hooks/useResponsiveValue/useResponsiveValue";

const justifyClasses = {
    start: cls.justifyStart,
    center: cls.justifyCenter,
    end: cls.justifyEnd,
    between: cls.justifyBetween,
    around: cls.justifyAround,
};

const alignClasses = {
    start: cls.alignStart,
    center: cls.alignCenter,
    end: cls.alignEnd,
    unset: cls.alignUnset,
};

const directionClasses = {
    column: cls.directionColumn,
    row: cls.directionRow,
};

const basisClasses = {
    100: cls.flexBasis100,
    70: cls.flexBasis70,
    50: cls.flexBasis50,
    30: cls.flexBasis30,
    25: cls.flexBasis25,
};

const shrinkClasses = {
    0: cls.shrink0,
    1: cls.shrink1,
    2: cls.shrink2,
};

export const defaultFlexTag = "div";

/**
 * @typedef {'start' | 'center' | 'end' | 'between'|'around'} FlexJustify
 * @typedef {'start' | 'center' | 'end' | 'unset'} FlexAlign
 * @typedef {'row' | 'column'} FlexDirection
 * @typedef {'nowrap' | 'wrap'} FlexWrap
 * @typedef {'0' | '1' | '2'} FlexShrink
 * @typedef {'100' | '70' | '50' | '30' | '25'} FlexBasis
 */

/**
 * @typedef {Object} FlexProps
 * @property {string} [className]
 * @property {FlexJustify} [justify]
 * @property {FlexAlign} [align]
 * @property {FlexDirection} [direction]
 * @property {number | string | Object} [gap]
 * @property {number | string | Object} [order]
 * @property {boolean} [max]
 * @property {boolean} [maxHeight]
 * @property {boolean} [maxWidth]
 * @property {FlexWrap} [wrap]
 * @property {FlexShrink} [shrink]
 * @property {FlexBasis} [basis]
 * @property {React.ElementType} [as]
 * @property {React.ReactNode} [children]
 */

/**
 * @param {FlexProps} props
 */
export const Flex = (props) => {
    const {
        className,
        children,
        justify = "start",
        align = "center",
        direction = "row",
        gap = 0,
        max,
        maxHeight,
        maxWidth,
        wrap = "nowrap",
        shrink,
        as: Tag = "div",
        order,
        basis,
        ...otherProps
    } = props;

    const resolvedGap = useFlexibleResponsiveValue(gap);
    const resolvedDirection = useFlexibleResponsiveValue(direction);
    const resolvedAlign = useFlexibleResponsiveValue(align);
    const resolvedJustify = useFlexibleResponsiveValue(justify);
    const resolvedMaxWidth = useFlexibleResponsiveValue(maxWidth);
    const resolvedHeight = useFlexibleResponsiveValue(maxHeight);
    const resolvedOrder = useFlexibleResponsiveValue(order);
    const resolvedWrap = useFlexibleResponsiveValue(wrap);
    const resolvedBasis = useFlexibleResponsiveValue(basis);

    const classes = [
        className,
        justifyClasses[resolvedJustify],
        alignClasses[resolvedAlign],
        directionClasses[resolvedDirection],
        basisClasses[resolvedBasis],
        cls[resolvedWrap],
        shrink && shrinkClasses[shrink],
    ];

    const mods = {
        [cls.max]: max,
        [cls.maxHeight]: resolvedHeight,
        [cls.maxWidth]: resolvedMaxWidth,
    };

    return (
        <Tag
            style={{ gap: resolvedGap, order: resolvedOrder }}
            className={classNames(cls.flex, mods, classes)}
            {...otherProps}
        >
            {children}
        </Tag>
    );
};
